import {
  Avatar,
  Box,
  Button,
  ColumnDatas,
  Table,
  Tooltip,
  Typography,
} from "fds";
import { useEffect, useRef, useState } from "react";
import "./index.scss";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import utilsCommon from "../../shared/utils/common";
import AdminFilter from "../../components/AdminFilter";
import { FilterData } from "../../types/adminFilter";
import useAdminLogStore from "../../redux/dispatcher/useAdminLogStore";
import { LogInfo } from "../../types/adminLog";
import EmptyFilterPage from "../../components/EmptyPage/EmptyFilterPage";
import { Workgroup } from "../../types/workgroup";
import useAdminWorkgroupStore from "../../redux/dispatcher/useAdminWorkgroupStore";
import utilsDocs from "../../shared/utils/docs";

const DocLog: React.FC = () => {
  const { t } = useTranslation();
  const { adminLogStore, getDocLog, getDocLogExcel } = useAdminLogStore();
  const { adminWorkgroupStore, getWorkgroupList } = useAdminWorkgroupStore();

  /**** State 모음 ****/
  const initFilterData = {
    fileName: "",
    userId: "",
    workgroupId: "",
    dateFilter: "1month",
    date: {
      start: utilsCommon.getStartPeriod("1month"),
      end: dayjs().endOf("day").valueOf(),
    },
    order: "datedesc",
    saveTypes: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
  } as FilterData;

  const [filterData, setFilterData] = useState<FilterData>(initFilterData);
  const [docLogs, setDocLogs] = useState([] as LogInfo[]);
  const pageCount = 50;
  const [nextPage, setNextPage] = useState(-1);
  const [workgroupList, setWorkgroupList] = useState([] as Workgroup[]);
  const [height, setHeight] = useState(window.innerHeight - 260);
  const tableRef = useRef<HTMLDivElement>(null);
  const [initDone, setInitDone] = useState(false);

  /**** UseEffect 모음 ****/
  useEffect(() => {
    getDocLogList();
    getWorkgroupList({
      orderBy: "workgroupName",
      orderByType: "ASC",
      status: -1,
      posPage: 1,
      pageRange: 1,
      rowRange: 0,
      searchDate: {
        start: 0,
        end: 0,
      },
      workgroupName: "",
      managerId: "",
      isDetail: "",
    });
    setInitDone(true);
  }, []);

  useEffect(() => {
    if (adminLogStore.docLog.posPage === 1) {
      setDocLogs(adminLogStore.docLog.logResults);
    } else {
      setDocLogs(docLogs.concat(adminLogStore.docLog.logResults));
    }
    if (adminLogStore.docLog.posPage * pageCount < adminLogStore.docLog.total) {
      setNextPage(adminLogStore.docLog.posPage + 1);
    } else {
      setNextPage(-1);
    }
  }, [adminLogStore.docLog]);

  useEffect(() => {
    if (adminWorkgroupStore.usingWorkgroup.workgroupList?.length > 0) {
      setWorkgroupList(adminWorkgroupStore.usingWorkgroup.workgroupList);
    }
  }, [adminWorkgroupStore.usingWorkgroup.workgroupList]);

  useEffect(() => {
    const handleResize = () => {
      if (tableRef.current) {
        const rect = tableRef.current.getBoundingClientRect();
        setHeight(window.innerHeight - rect.top - 72);
      }
    };

    const element = tableRef.current;

    const resizeObserver = new ResizeObserver(() => {
      if (element) {
        const rect = element.getBoundingClientRect();
        setHeight(window.innerHeight - rect.top - 72);
      }
    });

    if (element) {
      resizeObserver.observe(element);
    }
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (element) {
        resizeObserver.unobserve(element);
      }
    };
  }, []);

  useEffect(() => {
    initDone && getDocLogList();
  }, [filterData]);

  const getDocLogList = (page?: number) => {
    if (page !== -1) {
      getDocLog({
        posPage: page ?? 1,
        pageRange: 1,
        rowRange: pageCount,
        filterDocument: filterData.saveTypes ?? [
          0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11,
        ],
        filterWorkgroup: [],
        filterWebMeeting: [],
        filterChat: [],
        startDate:
          filterData?.date?.start ?? utilsCommon.getStartPeriod("1month"),
        endDate: filterData?.date?.end ?? dayjs().endOf("day").valueOf(),
        order: filterData.order + "",
        userId: filterData.userId + "",
        documentName: filterData.fileName + "",
        workgroupId: filterData.workgroupId ?? "",
      });
    }
  };

  const getAgentTypeStr = (agentType: number) => {
    const AgentStr = [
      "N/A",
      "Windows",
      "Mac",
      "Web",
      "Mobile",
      "Outlook",
      "FSM",
      "N/A",
    ];

    return AgentStr[agentType];
  };

  function transformFileList(data: any[] | undefined) {
    const dataTransformed = [] as ColumnDatas[];
    data &&
      data?.map((item: any, index: number) => {
        if (item && item?.workgroupId && item?.workgroupName) {
          const dataItemTransformed = {
            data: [
              {
                columnText: "",
              },
              {
                columnText: (
                  <Box alignItems="center">
                    <span style={{ minWidth: 24 }}>
                      {utilsDocs.renderDocIcon(item.documnetName, 24)}
                    </span>
                    <Tooltip message={item.documnetName}>
                      <Typography classes="mg ml-4" ellipsis>
                        {item.documnetName}
                      </Typography>
                    </Tooltip>
                  </Box>
                ),
              },
              {
                columnText: (
                  <Tooltip
                    message={
                      item.workgroupId === "NONEWORKGROUP"
                        ? "My Box"
                        : item.workgroupName
                    }
                  >
                    <Box alignItems="center" width={190}>
                      <Avatar
                        uniqueCode={item.workgroupId}
                        name={
                          item.workgroupId === "NONEWORKGROUP"
                            ? "My Box"
                            : item.workgroupName
                        }
                        shape="workgroup"
                      />
                      <Typography width={200} ellipsis classes="mg ml-8">
                        {item.workgroupId === "NONEWORKGROUP"
                          ? "My Box"
                          : item.workgroupName}
                      </Typography>
                    </Box>
                  </Tooltip>
                ),
              },
              {
                columnText: item.revisionNo,
              },
              {
                columnText:
                  item.historyType === 0
                    ? t(`log.filterWebMeeting.${item.saveType}`)
                    : item.historyType === 1
                    ? t(`log.filterDocument.${item.saveType}`)
                    : item.historyType === 2
                    ? t(`log.filterWorkgroup.${item.saveType}`)
                    : t(`log.filterChat.${item.saveType}`),
              },
              {
                columnText: (
                  <Tooltip
                    message={utilsCommon.getNameWithId(
                      item.userName,
                      item.userId
                    )}
                  >
                    <div className="overflow-hidden">
                      {utilsCommon.getNameWithId(item.userName, item.userId)}
                    </div>
                  </Tooltip>
                ),
              },
              {
                columnText: item.ip,
              },
              {
                columnText: getAgentTypeStr(item.agentType),
              },
              {
                columnText: utilsCommon.renderDateFormat(item.logDate),
              },
            ],
            id: item.logId,
          };
          dataTransformed.push(dataItemTransformed);
        }
      });
    return dataTransformed;
  }

  return (
    <Box classes="adminDocLog" direction="column">
      <Box classes="adminDocLog-title" justifyContent="space-between">
        <Typography type="h1">{t("admin.menu.log")}</Typography>
      </Box>
      <Box classes="adminDocLog-title" justifyContent="space-between">
        <AdminFilter
          initFilterData={initFilterData}
          filterData={filterData}
          setFilterData={setFilterData}
          workgroupList={workgroupList}
        />
        <Button
          type="primary"
          btnStyle="fill"
          label={t("admin.log.export")}
          onClick={() => {
            getDocLogExcel({
              posPage: 1,
              pageRange: 1,
              rowRange: pageCount,
              filterDocument: filterData.saveTypes ?? [
                0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11,
              ],
              startDate:
                filterData?.date?.start ?? utilsCommon.getStartPeriod("1month"),
              endDate: filterData?.date?.end ?? dayjs().endOf("day").valueOf(),
              order: filterData.order + "",
              userId: filterData.userId + "",
              documentName: filterData.fileName + "",
              workgroupId: filterData.workgroupId ?? "",
            });
          }}
          state={docLogs.length > 0 ? "default" : "disable"}
        />
      </Box>

      <Box ref={tableRef} classes="mg mt-12" direction="column">
        <Table
          width={"100%"}
          height={(docLogs.length === 0 ? 0 : height) + "px"}
          header={[
            {
              headerText: "",
              width: "24px",
              order: "",
              orderAction: () => {},
            },
            {
              headerText: t("admin.log.fileName"),
              width: "calc(100% - 1124px)",
              order: "",
              orderAction: () => {},
            },
            {
              headerText: t("admin.log.workgroupName"),
              width: "200px",
              order: "",
              orderAction: () => {},
            },
            {
              headerText: t("admin.log.currentVersion"),
              width: "80px",
              order: "",
              orderAction: () => {},
            },
            {
              headerText: t("admin.filter.type"),
              width: "200px",
              order: "",
              orderAction: () => {},
            },
            {
              headerText: t("admin.log.user"),
              width: "250px",
              order: "",
              orderAction: () => {},
            },
            {
              headerText: t("admin.log.ip"),
              width: "120px",
              order: "",
              orderAction: () => {},
            },
            {
              headerText: t("admin.log.platform"),
              width: "100px",
              order: "",
              orderAction: () => {},
            },
            {
              headerText: t("admin.log.date"),
              width: "150px",
              order: "",
              orderAction: () => {},
            },
          ]}
          data={transformFileList(docLogs)}
          totalCount={docLogs.length}
          endReached={() => {
            getDocLogList(nextPage);
          }}
          columnHeight="md"
        />
        {docLogs.length === 0 && <EmptyFilterPage />}
      </Box>
    </Box>
  );
};

export default DocLog;
